/** @typedef {'rfi' | 'assessment'} ContactCategory */

/** Split a legacy single-line contact name into first/last parts. */
export function parseLegacyContactName(fullName) {
  const trimmed = String(fullName ?? "").trim();
  if (!trimmed) return { first_name: "", last_name: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

/** Resolve a contact's display name from API / legacy shapes. */
export function getContactDisplayName(contact) {
  if (!contact) return "";
  const fromParts = [contact.first_name ?? contact.firstName, contact.last_name ?? contact.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromParts) return fromParts;
  return String(contact.displayName ?? contact.name ?? "").trim();
}

/** Resolve a contact's job title from API / legacy shapes. */
export function getContactDisplayTitle(contact) {
  if (!contact) return "";
  return String(
    contact.displayTitle ??
      contact.title ??
      contact.job_title ??
      contact.jobTitle ??
      contact.rb_department_title ??
      "",
  ).trim();
}

/** Whether a contact has enough data to show in the request-contact dropdown. */
export function isDisplayableApiContact(contact) {
  return Boolean(getContactDisplayName(contact));
}

/** Active unless the API explicitly marks the user inactive. Missing `is_active` stays eligible. */
export function isActiveProviderUser(contact) {
  return contact?.is_active !== false;
}

/** Named, active contact that can be offered on VQQ / VQA request forms. */
export function isEligibleRequestContact(contact) {
  return isDisplayableApiContact(contact) && isActiveProviderUser(contact);
}

/** Stable option id shared by multi-select options and selected chips. */
export function getContactOptionId(contact, index = 0) {
  const email = String(contact?.email ?? "").trim();
  if (email) return email;
  const providerUserId = contact?.provider_user_id ?? contact?.id;
  if (providerUserId != null && providerUserId !== "") return String(providerUserId);
  const nameKey = [contact?.first_name ?? contact?.firstName, contact?.last_name ?? contact?.lastName]
    .filter(Boolean)
    .join("-");
  if (nameKey) return nameKey;
  return `contact-${index}`;
}

/** Normalize provider_user / legacy vendor contact fields for request forms. */
export function normalizeApiContact(contact, index = 0) {
  if (!contact) return contact;
  const parsedLegacy = parseLegacyContactName(contact.name);
  const first_name = String(contact.first_name ?? contact.firstName ?? parsedLegacy.first_name ?? "").trim();
  const last_name = String(contact.last_name ?? contact.lastName ?? parsedLegacy.last_name ?? "").trim();
  const title = getContactDisplayTitle(contact);
  return {
    ...contact,
    id: getContactOptionId(contact, index),
    first_name,
    last_name,
    firstName: first_name,
    lastName: last_name,
    title,
    job_title: contact.job_title ?? contact.jobTitle ?? title,
    displayName: getContactDisplayName({ ...contact, first_name, last_name }),
    displayTitle: title,
  };
}

/** Build a request-form contact from vendor-level legacy contact fields. */
export function buildLegacyProviderContact(provider) {
  const email = String(provider?.contact_email ?? "").trim();
  if (!email) return null;
  const name = String(provider?.contact_name ?? "").trim();
  const parsed = parseLegacyContactName(name);
  return normalizeApiContact({
    email,
    name,
    first_name: parsed.first_name,
    last_name: parsed.last_name,
  });
}

export const CONTACT_CATEGORY = {
  RFI: "rfi",
  ASSESSMENT: "assessment",
};

/**
 * Which contact designation categories a vendor sub-role may hold.
 * @param {string[]|undefined} subRoles
 */
export function getEligibleCategoriesFromSubRoles(subRoles) {
  const roles = subRoles ?? [];
  const has = (name) => roles.some((r) => r === name || r?.includes?.(name));

  if (has("Vendor Admin")) {
    return [CONTACT_CATEGORY.RFI, CONTACT_CATEGORY.ASSESSMENT];
  }
  if (roles.some((r) => r?.includes?.("RFI Manager"))) {
    return [CONTACT_CATEGORY.RFI];
  }
  if (has("VQA Manager")) {
    return [CONTACT_CATEGORY.ASSESSMENT];
  }
  return [];
}

/** @param {object} row @param {'rfi'|'assessment'} category */
export function getCategoryBadgeValue(row, category) {
  if (!row) return "—";
  if (category === CONTACT_CATEGORY.RFI) {
    if (row.rfi_is_primary) return "Primary";
    if (row.rfi_request_contact_status) return "Yes";
    return "—";
  }
  if (row.assessment_is_primary) return "Primary";
  if (row.assessment_request_contact_status) return "Yes";
  return "—";
}

/** @param {object} row @param {'rfi'|'assessment'} category @returns {'none'|'request'|'primary'} */
export function getContactDesignationFromRow(row, category) {
  if (!row) return "none";
  if (category === CONTACT_CATEGORY.RFI) {
    if (row.rfi_is_primary) return "primary";
    if (row.rfi_request_contact_status) return "request";
    return "none";
  }
  if (row.assessment_is_primary) return "primary";
  if (row.assessment_request_contact_status) return "request";
  return "none";
}

/** @param {'none'|'request'|'primary'} designation */
export function designationToApiFields(designation, category) {
  const isRequest = designation === "request" || designation === "primary";
  const isPrimary = designation === "primary";
  if (category === CONTACT_CATEGORY.RFI) {
    return {
      rfi_request_contact_status: isRequest,
      rfi_is_primary: isPrimary,
    };
  }
  return {
    assessment_request_contact_status: isRequest,
    assessment_is_primary: isPrimary,
  };
}

/**
 * Filter provider_user list to designated contacts for a request form category.
 * @param {object[]} contacts
 * @param {'rfi'|'assessment'} category
 */
export function filterContactsForCategory(contacts, category) {
  if (!contacts?.length) return [];
  const requestField =
    category === CONTACT_CATEGORY.RFI
      ? "rfi_request_contact_status"
      : "assessment_request_contact_status";
  const primaryField =
    category === CONTACT_CATEGORY.RFI ? "rfi_is_primary" : "assessment_is_primary";

  return contacts
    .filter((c) => isActiveProviderUser(c) && (c[requestField] || c[primaryField]))
    .map((c, index, arr) =>
      normalizeApiContact(
        {
          ...c,
          isCategoryPrimary: Boolean(c[primaryField]),
        },
        index,
      ),
    )
    .sort((a, b) => {
      if (a.isCategoryPrimary && !b.isCategoryPrimary) return -1;
      if (!a.isCategoryPrimary && b.isCategoryPrimary) return 1;
      const nameA = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim();
      const nameB = `${b.first_name ?? ""} ${b.last_name ?? ""}`.trim();
      return nameA.localeCompare(nameB);
    });
}

/** @param {object[]} allUsers @param {object} user @param {'rfi'|'assessment'} category */
export function isLastCategoryContact(allUsers, user, category) {
  const requestField =
    category === CONTACT_CATEGORY.RFI
      ? "rfi_request_contact_status"
      : "assessment_request_contact_status";
  const count = (allUsers ?? []).filter((u) => u[requestField]).length;
  return count <= 1 && user?.[requestField];
}

export const CATEGORY_LABELS = {
  [CONTACT_CATEGORY.RFI]: "VQQ",
  [CONTACT_CATEGORY.ASSESSMENT]: "Assessment",
};

export const MIN_CONTACT_ERROR = {
  [CONTACT_CATEGORY.RFI]:
    "Each organization must have at least one VQQ request contact. Please assign another contact before removing this one.",
  [CONTACT_CATEGORY.ASSESSMENT]:
    "Each organization must have at least one Assessment request contact. Please assign another contact before removing this one.",
};

export const MIN_PRIMARY_ERROR = {
  [CONTACT_CATEGORY.RFI]:
    "Each organization must have at least one Primary VQQ contact.",
  [CONTACT_CATEGORY.ASSESSMENT]:
    "Each organization must have at least one Primary Assessment contact.",
};

/** Eligible designation categories when submitting New Request Vendor. */
export function getEligibleCategoriesForNewVendorRequest(showRfi, showAssessment) {
  const categories = [];
  if (showRfi) categories.push(CONTACT_CATEGORY.RFI);
  if (showAssessment) categories.push(CONTACT_CATEGORY.ASSESSMENT);
  return categories;
}

/**
 * Designation level for a contact created during New Request Vendor.
 * @param {boolean} isPrimary - preferred vs additional contact
 */
/**
 * Designation level for a contact created during New Request Vendor.
 * @param {boolean} isPrimary - preferred vs additional contact
 */
export function getDesignationsForNewVendorContact(isPrimary, showRfi, showAssessment) {
  const level = isPrimary ? "primary" : "request";
  return {
    rfiDesignation: showRfi ? level : "none",
    assessmentDesignation: showAssessment ? level : "none",
  };
}

export function getDefaultApiContactsSelection(apiContacts) {
  const displayable = (apiContacts ?? []).filter(isEligibleRequestContact);
  if (!displayable.length) return [];
  const primary = displayable.find((c) => c.isCategoryPrimary) || displayable[0];
  return [mapApiContactToMultiSelectOption(primary)];
}

/**
 * Contacts offered on VQQ / VQA request forms.
 * Inactive users are omitted from the selectable list. If the vendor has users
 * but none are eligible, returns [] so the form shows the required "add a
 * contact" UI — do not fall back to the org-level legacy contact in that case,
 * or the form would think a contact is already available.
 */
export function getRequestFormContacts(provider, category) {
  if (!provider) return [];

  const providerUsers = provider.provider_user ?? [];
  if (providerUsers.length > 0) {
    return filterContactsForCategory(providerUsers, category).filter(
      isEligibleRequestContact,
    );
  }

  const legacyContact = buildLegacyProviderContact(provider);
  return legacyContact && isEligibleRequestContact(legacyContact)
    ? [legacyContact]
    : [];
}

export function mapApiContactToMultiSelectOption(contact, index = 0) {
  const normalized = normalizeApiContact(contact, index);
  return {
    ...normalized,
    id: normalized.id ?? getContactOptionId(normalized, index),
    displayName: normalized.displayName ?? getContactDisplayName(normalized),
    displayTitle: normalized.displayTitle ?? getContactDisplayTitle(normalized),
    firstName: normalized.first_name,
    lastName: normalized.last_name,
  };
}
